import {
  Button,
  Cell,
  StandaloneLink,
  TextInput,
} from '@zeiss/beyond-online-react';
import {
  Box,
  ButtonGroup,
  Grid,
  ReadOnlyField,
  Section,
  SectionActionCell,
  SectionHeadline,
  SectionText,
} from '@zeiss/lib-components';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';

import { DeleteApplicationListModal, ManagingApplicationsResult } from './';
import { ApplicationListGeneralState, OutletContextType } from './types';
import { getGeneralIsChanged, getManagingApplicationsIsChanged } from './utils';
import { MAX_CHARS_FOR_NAME } from 'src/constants';
import { showToast } from 'src/helpers/showToast';
import { useEditApplicationList } from 'src/hooks/api';
import { ApplicationSearch } from 'src/modules/TenantManagement/components/ApplicationSearch';
import {
  UserSearch,
  UserSearchResultItems,
} from 'src/modules/TenantManagement/components/UserSearch';
import { SearchUsersResponse } from 'src/types/rest/managementPortalApi';
import { ApplicationListResponse } from 'src/types/rest/tenant';
import { Application } from 'src/types/tenantManagement';

export const Details = () => {
  const { t } = useTranslation(['common']);
  const {
    applicationListId,
    applicationListManagers,
    applicationListName,
    applicationListOwners,
    canDelete,
    canUpdateManagers,
    canUpdateManagingApplications,
    canUpdateName,
    canUpdateOwners,
    managingApplications: applications,
  } = useOutletContext<OutletContextType>();

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
    React.useState(false);

  const [general, setGeneral] = React.useState<ApplicationListGeneralState>({
    managers: applicationListManagers ?? [],
    name: applicationListName,
    owners: applicationListOwners ?? [],
  });

  const [managingApplications, setManagingApplications] = React.useState<
    ApplicationListResponse['managingApplications']
  >(applications ?? []);

  const { isPending: isEditGeneralPending, mutate: editGeneral } =
    useEditApplicationList(applicationListId);

  const {
    isPending: isEditManagingApplicationsPending,
    mutate: editManagingApplications,
  } = useEditApplicationList(applicationListId);

  const onChange: React.ChangeEventHandler<HTMLInputElement> = (e) =>
    setGeneral((prev) => ({
      ...prev,
      error: '',
      name: e.target.value,
    }));

  const handleOwnerSelection = (owners: SearchUsersResponse[]) => {
    setGeneral((prev) => ({
      ...prev,
      owners,
    }));
  };
  const handleManagerSelection = (managers: SearchUsersResponse[]) => {
    setGeneral((prev) => ({
      ...prev,
      managers,
    }));
  };
  const handleManagingApplicationSelection = (
    managingApplication: Application,
  ) => {
    setManagingApplications((prev) => {
      if (prev?.find(({ id }) => id === managingApplication.id)) {
        return prev;
      }

      return [...(prev ?? []), managingApplication];
    });
  };

  const removeManagingApplication = (applicationId: string) => {
    setManagingApplications((prev) =>
      prev?.filter(({ id }) => id !== applicationId),
    );
  };

  const showConfirmationModal = () => {
    setIsConfirmationModalOpen(true);
  };

  const closeConfirmationModal = () => {
    setIsConfirmationModalOpen(false);
  };

  const cancelGeneral = () => {
    setGeneral({
      managers: applicationListManagers ?? [],
      name: applicationListName,
      owners: applicationListOwners ?? [],
    });
  };

  const cancelManagingApplications = () => {
    setManagingApplications(applications ?? []);
  };

  const saveGeneral = () => {
    const { managers, name, owners } = general;

    editGeneral(
      {
        managerIds: managers?.map(({ id }) => id),
        name,
        ownerIds: owners?.map(({ id }) => id),
      },
      {
        onError: (error) => {
          if (error instanceof Response && error.status === 422) {
            return setGeneral((prev) => ({
              ...prev,
              error: t('error.applicationListExists'),
            }));
          }

          showToast({
            props: {
              title: t('common:notification.errorX', {
                action: t('common:action.updated'),
                x: t('common:glossary.applicationList'),
              }),
              type: 'error',
            },
          });
        },
        onSuccess: () => {
          showToast({
            props: {
              title: t('common:notification.successX', {
                action: t('common:action.updated'),
                x: t('common:glossary.applicationList'),
              }),
              type: 'success',
            },
          });
        },
      },
    );
  };

  const saveManagingApplications = () => {
    editManagingApplications(
      {
        managingApplicationIds: managingApplications?.map(({ id }) => id),
        name: applicationListName,
      },
      {
        onError: () => {
          showToast({
            props: {
              title: t('common:notification.errorX', {
                action: t('common:action.updated'),
                x: t('common:glossary.applicationList'),
              }),
              type: 'error',
            },
          });
        },
        onSuccess: () => {
          showToast({
            props: {
              title: t('common:notification.successX', {
                action: t('common:action.updated'),
                x: t('common:glossary.applicationList'),
              }),
              type: 'success',
            },
          });
        },
      },
    );
  };

  const existingGeneral: ApplicationListGeneralState = {
    managers: applicationListManagers ?? [],
    name: applicationListName,
    owners: applicationListOwners ?? [],
  };

  const hasUnsavedGeneralChanges = getGeneralIsChanged({
    current: general,
    initial: existingGeneral,
  });

  const hasUnsavedManagingApplicationsChanges =
    getManagingApplicationsIsChanged({
      current: managingApplications,
      initial: applications,
    });

  const { error, managers, name, owners } = general;

  const nameLabel = t('common:label.xName', {
    x: t('common:glossary.applicationList'),
  });

  const managersLabel = t('common:label.xManagers', {
    x: t('common:glossary.applicationList'),
  });

  const ownersLabel = t('common:label.xOwners', {
    x: t('common:glossary.applicationList'),
  });

  return (
    <>
      <Section>
        <Cell.Basic colSpan={[4, 6, 4]}>
          <SectionHeadline headline={t('common:title.general')} size="s" />
        </Cell.Basic>

        <Cell.Basic colSpan={[4, 6, 8]}>
          <Box mode="static" spacing="l">
            <Grid>
              <Cell.Basic colSpan={[4, 6, 6]}>
                {canUpdateName ? (
                  <TextInput
                    autoComplete="off"
                    error={error ? error : undefined}
                    label={nameLabel}
                    maxLength={MAX_CHARS_FOR_NAME}
                    onChange={onChange}
                    value={name}
                  />
                ) : (
                  <ReadOnlyField text={name} title={nameLabel} />
                )}
              </Cell.Basic>
            </Grid>
          </Box>

          <Box
            data-testid="applicationListManagersBox"
            mode="static"
            spacing="l"
          >
            {canUpdateManagers ? (
              <UserSearch
                colSpan={[4, 6, 6]}
                handleUserSelect={handleManagerSelection}
                id="applicationListManagers"
                label={managersLabel}
                restrictToZeissEmailDomain
                selectedUsers={managers}
              />
            ) : (
              <ReadOnlyField title={managersLabel}>
                <UserSearchResultItems disabled selectedUsers={managers} />
              </ReadOnlyField>
            )}
          </Box>

          <Box data-testid="applicationListOwnersBox">
            {canUpdateOwners ? (
              <UserSearch
                colSpan={[4, 6, 6]}
                handleUserSelect={handleOwnerSelection}
                id="applicationListOwners"
                label={ownersLabel}
                minSelectedUser={1}
                restrictToZeissEmailDomain
                selectedUsers={owners}
              />
            ) : (
              <ReadOnlyField title={ownersLabel}>
                <UserSearchResultItems disabled selectedUsers={owners} />
              </ReadOnlyField>
            )}
          </Box>

          {hasUnsavedGeneralChanges ? (
            <ButtonGroup spacing="s" variant="form">
              <Button onClick={cancelGeneral} variant="secondary">
                {t('common:action.cancel')}
              </Button>
              <Button
                disabled={!!error}
                loading={isEditGeneralPending}
                onClick={saveGeneral}
              >
                {t('common:action.saveChanges')}
              </Button>
            </ButtonGroup>
          ) : null}
        </Cell.Basic>
      </Section>

      <Section>
        <Cell.Basic colSpan={[4, 6, 4]}>
          <SectionHeadline
            headline={t('common:glossary.managingApplication_other')}
            size="s"
          />
          <SectionText>
            {canUpdateManagingApplications
              ? t('common:text.managingApplications')
              : t('common:text.managingApplicationsReadOnly')}
          </SectionText>
        </Cell.Basic>

        <Cell.Basic colSpan={[4, 6, 8]}>
          <Box
            data-testid="applicationListManagingApplicationsBox"
            mode="static"
            spacing="l"
          >
            <Grid>
              <Cell.Basic colSpan={[4, 6, 6]}>
                {canUpdateManagingApplications ? (
                  <ApplicationSearch
                    onSelectApplication={handleManagingApplicationSelection}
                    selectedApplications={managingApplications ?? []}
                  />
                ) : null}
                <ManagingApplicationsResult
                  disabled={!canUpdateManagingApplications}
                  managingApplications={managingApplications ?? []}
                  onDeselect={removeManagingApplication}
                />
              </Cell.Basic>
            </Grid>
          </Box>

          {hasUnsavedManagingApplicationsChanges ? (
            <ButtonGroup spacing="s" variant="form">
              <Button onClick={cancelManagingApplications} variant="secondary">
                {t('common:action.cancel')}
              </Button>
              <Button
                loading={isEditManagingApplicationsPending}
                onClick={saveManagingApplications}
              >
                {t('common:action.saveChanges')}
              </Button>
            </ButtonGroup>
          ) : null}
        </Cell.Basic>
      </Section>

      {canDelete ? (
        <Section>
          <Cell.Basic colSpan={[4, 6, 4]}>
            <SectionHeadline
              headline={t('common:action.deleteX', {
                x: t('common:glossary.applicationList'),
              })}
              size="s"
            />
            <SectionText>
              {t('common:text.deleteXhere', {
                x: t('common:glossary.applicationList'),
              })}
            </SectionText>
          </Cell.Basic>

          <SectionActionCell colSpan={[4, 6, 8]}>
            <Box>
              <StandaloneLink href="" onClick={showConfirmationModal} size="xs">
                {t('common:action.deleteX', {
                  x: applicationListName,
                })}
              </StandaloneLink>
            </Box>
          </SectionActionCell>
        </Section>
      ) : null}

      <DeleteApplicationListModal
        data={{ applicationListId, applicationListName }}
        isOpen={isConfirmationModalOpen}
        onClose={closeConfirmationModal}
      />
    </>
  );
};
